import { useState, type ReactNode } from 'react';
import { Archive, History, ShieldAlert } from 'lucide-react';
import { Banner, Button, Card, EmptyState, LoadMore, TableSkeleton } from '@/components/ui';
import { RestoreBackupDialog } from '@/components/sites/RestoreBackupDialog';
import type { useSiteBackups } from '@/hooks/useSites';
import { formatBytes, formatDateTime } from '@/utils/format';
import { backupSlotLabel } from '@/utils/backups';
import { BACKUP_KIND_LABELS, BACKUP_VERIFICATION } from '@/utils/labels';
import type { SiteBackupView, SiteDetail } from '@/types/api';

/** A site's backups, newest first, with whether each was checked and whether it lives off the node. */
export function BackupsTab({
  site,
  mayRestore,
  query,
}: {
  readonly site: SiteDetail;
  readonly mayRestore: boolean;
  /** Owned by the page, which also shows the total on the tab. */
  readonly query: ReturnType<typeof useSiteBackups>;
}): ReactNode {
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const [restoring, setRestoring] = useState<SiteBackupView | null>(null);

  return (
    <div className="stack">
      {!site.hasVerifiedBackup ? (
        <Banner tone="warning" icon={ShieldAlert}>
          Нет копий, проверенных восстановлением. Удаление сайта недоступно.
        </Banner>
      ) : null}

      <Card flush>
        {query.isPending ? (
          <TableSkeleton rows={4} columns={5} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Archive} message="Копий ещё нет" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Создана</th>
                  <th scope="col">Тип</th>
                  <th scope="col">Проверка</th>
                  <th scope="col" className="numeric">
                    Размер
                  </th>
                  <th scope="col">Внешняя копия</th>
                  {mayRestore ? <th scope="col" aria-label="Действия" /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((backup) => {
                  const state = BACKUP_VERIFICATION[backup.verification] ?? { label: backup.verification, tone: 'status-unknown' };

                  return (
                    <tr key={backup.id}>
                      <td className="nowrap" title={backup.createdAt}>
                        <div className="stack stack-tight">
                          <span>{formatDateTime(backup.createdAt)}</span>
                          <span className="text-sm muted">{backupSlotLabel(backup)}</span>
                        </div>
                      </td>
                      <td className="muted nowrap" title={backup.kind}>
                        {BACKUP_KIND_LABELS[backup.kind] ?? backup.kind}
                      </td>
                      <td>
                        <span className={`status ${state.tone}`}>{state.label}</span>
                        {backup.restoreTestedAt ? (
                          // The weekly drill (S8): not only intact, brought back.
                          <span className={`text-sm ${backup.restoreTestOk ? 'muted' : 'tone-down'}`}>
                            {' '}
                            · {backup.restoreTestOk ? 'восстановление проверено' : 'восстановление не прошло'}{' '}
                            {formatDateTime(backup.restoreTestedAt)}
                          </span>
                        ) : null}
                      </td>
                      <td className="numeric nowrap">{formatBytes(backup.sizeBytes)}</td>
                      <td className={backup.offsiteConfirmed ? '' : 'tone-degraded'}>{backup.offsiteConfirmed ? 'есть' : 'нет'}</td>
                      {mayRestore ? (
                        <td className="numeric">
                          {backup.verification === 'verified' ? (
                            <Button size="sm" variant="ghost" icon={History} onClick={() => setRestoring(backup)}>
                              Восстановить
                            </Button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <LoadMore
        shown={rows.length}
        total={query.data?.pages[0]?.total ?? null}
        hasMore={query.hasNextPage}
        busy={query.isFetchingNextPage}
        onMore={() => void query.fetchNextPage()}
      />

      <RestoreBackupDialog site={site} backup={restoring} onClose={() => setRestoring(null)} />
    </div>
  );
}
